import moment from "moment";
import {ContestStatus} from "../../constants/PickXEnums";
import GlobalConst from "../../constants/GlobalConst";
import {isPasswordValid} from "../../utils/PasswordUtils";
import {useDispatch, useSelector} from "react-redux";
import {makeSelectGamesByIds} from "../../stores/sports/sports.selectors";
import LoginPopUp from "../../views/PopUps/LoginPopUp/LoginPopUp";
import InvalidEntryFeePopUp from "../../views/PopUps/InvalidEntryFeePopUp/InvalidEntryFeePopUp";
import UnAvailableContestsPopUp from "../../views/PopUps/UnAvailableContestsPopUp/UnAvailableContestsPopUp";
import GeneralAlertPopUp from "../../views/PopUps/GeneralPopUp/GeneralAlertPopUp";
import {submitParlayAsync} from "../../stores/pickX/pickX.thunks";
import useAppModalService from "../../hooks/useAppModalService";
import {selectUserIsAuthenticated} from "../../stores/account/account.selectors";
import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FormattedMessage} from "react-intl";

const usePreSubmitParlayService =({parlay, contests, selectedEntryFee, focusToFeeInput})=>{
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {openModal} = useAppModalService();
    const isUserAuthenticated = useSelector(selectUserIsAuthenticated);
    const [parlaySubmitted, setParlaySubmitted] = useState(false);
    const gameIds = useMemo(() => {
        const ids = [...new Set(contests?.map(c => c.GameId) ?? [])];
        return ids.sort(); // Ensure stable order for shallow equality
    }, [contests]);
    const selectGames = useMemo(() => makeSelectGamesByIds(gameIds), [gameIds]);
    const games = useSelector(selectGames);
    const hasStarted = games.some(game => moment(game?.DateTime).isBefore(moment()));
    const isCancelled = contests.some(contest => contest?.Status === ContestStatus.Cancelled);
    const isCompleted = contests.some(contest => contest?.Status === ContestStatus.Completed);
    const areContestsUnavailable = hasStarted || isCancelled || isCompleted;
    const isEntryFeeInvalid = (!selectedEntryFee && !focusToFeeInput) ? false : isNaN(selectedEntryFee) || selectedEntryFee % 1 !== 0 || selectedEntryFee < 0;
    const isPwdProtected = GlobalConst.FEATURES.PASSWORD_PROTECTED && !isPasswordValid(localStorage.getItem("password"));
    const disableSubmitParlay = !parlay?.canSubmit || parlaySubmitted;

    const submitParlay = async ()=>{
        if (!parlay) return;
        if (!isUserAuthenticated) {
            return await openModal('login', 'info', {
                title: 'Login',
                modalComponent: LoginPopUp,
            });
        }

        if(!parlay.canSubmit){
            await openModal('unCompletedSelectionPopUp', 'info', {
                title: <FormattedMessage id={`popUp.unCompletedSelectionPopUp.${parlay.PayoutModel}.title`}/>,
                details: <FormattedMessage id={`popUp.unCompletedSelectionPopUp.${parlay.PayoutModel}.subTitle`}/>,
                modalComponent: GeneralAlertPopUp,
            })
        }

        if (isEntryFeeInvalid) {
            return await openModal('invalidEntryFee', 'info', {
                title: 'Invalid Entry Fee',
                modalComponent: InvalidEntryFeePopUp,
                onOk: () => {
                    setTimeout(() => {
                        focusToFeeInput();
                    }, 100);
                },
            });
        }

        if (areContestsUnavailable) {
            return await openModal('unAvailableContestsPopUp', 'info', {
                title: 'UnAvailable Contests',
                modalComponent: UnAvailableContestsPopUp,
                onOk: () => {
                    navigate('/lobby');
                },
            });
        }

        if (isPwdProtected) {
            return await openModal('passwordPopUp', 'error', {
                title: <FormattedMessage id={`restrictedAccess.title`}/>,
                details: <FormattedMessage id={`restrictedAccess.pleaseProvidePassword`}/>,
                modalComponent: GeneralAlertPopUp,
            });
        }

        setParlaySubmitted(true);
        await dispatch(submitParlayAsync({ parlay: parlay.toDto() }));
        setParlaySubmitted(false);
    }
    return {
        submitParlay,
        parlaySubmitted,
        disableSubmitParlay
    }
}
export default usePreSubmitParlayService;